/**
 * Global Accounting Data Cache
 * 
 * This module provides a persistent in-memory cache for accounting data
 * that survives component unmounts and page navigation.
 * 
 * Key features:
 * - Data persists across page navigation (no refetch needed)
 * - Configurable TTL per cache entry
 * - Manual invalidation when data is modified
 * - Separate caches for different data types (bills, invoices, transactions, etc.)
 */

// Default cache TTL: 5 minutes (data stays valid for navigation)
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

// Cache storage - persists in memory across component lifecycles
const dataCache = new Map();
const cacheTimestamps = new Map();
const cacheTTLs = new Map();

/**
 * Get cached data if still valid
 * @param {string} cacheKey - Unique key for the cached data
 * @returns {any|null} - Cached data or null if expired/not found
 */
export const getCachedData = (cacheKey) => {
  if (!dataCache.has(cacheKey)) {
    return null;
  }

  const timestamp = cacheTimestamps.get(cacheKey);
  const ttl = cacheTTLs.get(cacheKey) || DEFAULT_CACHE_TTL;
  const now = Date.now();

  if (now - timestamp > ttl) {
    // Cache expired, remove it
    dataCache.delete(cacheKey);
    cacheTimestamps.delete(cacheKey);
    cacheTTLs.delete(cacheKey);
    return null;
  }

  return dataCache.get(cacheKey);
};

/**
 * Set data in cache
 * @param {string} cacheKey - Unique key for the cached data
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
 */
export const setCachedData = (cacheKey, data, ttl = DEFAULT_CACHE_TTL) => {
  dataCache.set(cacheKey, data);
  cacheTimestamps.set(cacheKey, Date.now());
  cacheTTLs.set(cacheKey, ttl);
};

/**
 * Check if cache entry exists and is valid
 * @param {string} cacheKey - Unique key for the cached data
 * @returns {boolean}
 */
export const hasCachedData = (cacheKey) => {
  return getCachedData(cacheKey) !== null;
};

/**
 * Invalidate specific cache entry
 * @param {string} cacheKey - Unique key for the cached data
 */
export const invalidateCache = (cacheKey) => {
  dataCache.delete(cacheKey);
  cacheTimestamps.delete(cacheKey);
  cacheTTLs.delete(cacheKey);
};

/**
 * Invalidate all cache entries matching a prefix
 * Useful for invalidating all bills when a bill is modified
 * @param {string} prefix - Cache key prefix to match
 */
export const invalidateCacheByPrefix = (prefix) => {
  const keysToDelete = [];
  for (const key of dataCache.keys()) {
    if (key.startsWith(prefix)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach((key) => {
    dataCache.delete(key);
    cacheTimestamps.delete(key);
    cacheTTLs.delete(key);
  });
};

/**
 * Clear all cached data
 */
export const clearAllCache = () => {
  dataCache.clear();
  cacheTimestamps.clear();
  cacheTTLs.clear();
};

/**
 * Get cache statistics (for debugging)
 * @returns {object}
 */
export const getCacheStats = () => {
  const stats = {
    totalEntries: dataCache.size,
    entries: [],
  };

  for (const [key, value] of dataCache.entries()) {
    const timestamp = cacheTimestamps.get(key);
    const ttl = cacheTTLs.get(key) || DEFAULT_CACHE_TTL;
    const age = Date.now() - timestamp;
    const remainingTTL = Math.max(0, ttl - age);

    stats.entries.push({
      key,
      dataSize: Array.isArray(value) ? value.length : typeof value,
      ageSeconds: Math.round(age / 1000),
      remainingTTLSeconds: Math.round(remainingTTL / 1000),
      isExpired: remainingTTL === 0,
    });
  }

  return stats;
};

// Cache key generators for consistent key creation
export const CacheKeys = {
  // Bills/Invoices
  bills: (brandsKey) => `bills_${brandsKey}`,
  invoices: (brandsKey) => `invoices_${brandsKey}`,
  billCredits: (brandsKey) => `bill_credits_${brandsKey}`,
  invoiceCredits: (brandsKey) => `invoice_credits_${brandsKey}`,
  
  // Transactions
  transactions: (brandsKey, types, dateRange) => 
    `transactions_${brandsKey}_${types || 'all'}_${dateRange || 'all'}`,
  bankStatements: (brandsKey, dateRange) => 
    `bank_statements_${brandsKey}_${dateRange || 'all'}`,
  
  // History
  billHistory: (brandsKey) => `bill_history_${brandsKey}`,
  invoiceHistory: (brandsKey) => `invoice_history_${brandsKey}`,
  
  // Reports
  upcomingBills: (brandsKey, dateRange) => 
    `upcoming_bills_${brandsKey}_${dateRange || 'all'}`,
  upcomingInvoices: (brandsKey, dateRange) => 
    `upcoming_invoices_${brandsKey}_${dateRange || 'all'}`,
  
  // Partners
  vendors: (brandsKey) => `vendors_${brandsKey}`,
  customers: (brandsKey) => `customers_${brandsKey}`,
};

// Export default TTLs for different data types
export const CacheTTL = {
  SHORT: 1 * 60 * 1000,    // 1 minute - for rapidly changing data
  MEDIUM: 5 * 60 * 1000,   // 5 minutes - default for most data
  LONG: 15 * 60 * 1000,    // 15 minutes - for rarely changing data
  VERY_LONG: 30 * 60 * 1000, // 30 minutes - for static data
};

export default {
  getCachedData,
  setCachedData,
  hasCachedData,
  invalidateCache,
  invalidateCacheByPrefix,
  clearAllCache,
  getCacheStats,
  CacheKeys,
  CacheTTL,
};

